import * as React from 'react'
import { useNavigate } from 'react-router-dom'

import { observer } from 'mobx-react'

import { useLazyRef } from 'modules/core/hooks/useLazyRef'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import { employeeUser } from 'modules/core/store/collections/users'
import type { FlashType } from 'modules/core/store/models/app'
import app from 'modules/core/store/models/app'

import DatesAndDetails from 'modules/contracts/components/FlowPage/Steps/DatesAndDetails'
import RoleLevelSalary from 'modules/contracts/components/FlowPage/Steps/RoleLevelSalary'
import ContractFlow from 'modules/contracts/lib/ContractFlow'
import { useContractFlow } from 'modules/contracts/lib/contractFlowContext'
import policyAuthorizations from 'modules/permissions/store/collections/policyAuthorizations'

import { FlowPageContent } from '../FlowPage/_FlowPageContent'
import Footer from './_Footer'
import DatesDetailsStep from './lib/DatesDetailsStep'
import RoleLevelSalaryStep from './lib/RoleLevelSalaryStep'
import { translations as datesStepTranslations } from './lib/datesDetailsTranslations'
import { getRequestPromoteFlowForm } from './lib/requestPromoteFlowForm'
import { translations as roleLevelSalaryTranslations } from './lib/roleLevelSalaryTranslations'
import { translations as templateTranslations } from './lib/templateTranslations'

const RequestPromoteFlowContent = () => {
  const navigate = useNavigate()
  const { navigation } = useNavigation()
  const {
    employee,
    contractVersion,
    compensations,
    contractVersionPolicy,
    legalEntityContractData,
  } = useContractFlow()
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const form = useLazyRef(() =>
    getRequestPromoteFlowForm(
      contractVersion,
      employee,
      contractVersionPolicy,
      policyAuthorizations
    )
  ).current
  const onClose = () => {
    navigate(
      navigation.employees.id.contractVersions.fullPath({
        id: employee.id.toString(),
      })
    )
  }
  const [isCurrentStepCompleted, setIsCurrentStepCompleted] = React.useState(false)

  const requestPromoteContractFlow = useLazyRef(
    () =>
      new ContractFlow([
        new DatesDetailsStep(
          (
            <DatesAndDetails
              step={1}
              type="request_promote"
              form={form}
              translations={datesStepTranslations()}
              setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            />
          )
        ),
        new RoleLevelSalaryStep(
          (
            <RoleLevelSalary
              step={2}
              type="request_promote"
              form={form}
              translations={roleLevelSalaryTranslations()}
              setIsCurrentStepCompleted={setIsCurrentStepCompleted}
            />
          )
        ),
      ])
  ).current

  const showFlashMessage = (type: FlashType, message: string) => {
    app.setFlash({
      type,
      message,
    })
  }

  const onSubmit = () => {
    setIsSubmitting(true)
    const api = ApiClient()
    const data = form.serialized
    data.additional_compensations = compensations.map((compensation) => compensation.attributes)
    data.employee_id = employee.id

    api
      .post(`/api/rest/contracts/contract_version_requests/create_promote_request`, data)
      .then((newContract) => {
        showFlashMessage(
          'correct',
          i18n.t('contract_versions.flows.request_promote.confirmation', {
            employeeFullName: employeeUser(employee).fullName(),
          })
        )
        navigate(
          navigation.employees.id.contractVersions.contractVersionId.fullPath({
            id: employee.id.toString(),
            contractVersionId: newContract.id.toString(),
          })
        )
      })
      .catch(() => {
        showFlashMessage('wrong', i18n.t('contract_versions.flows.errors.unknown'))
        //TODO: Decide with product how to show specific errors
        setIsSubmitting(false)
      })
  }

  return (
    <FlowPageContent
      translations={templateTranslations()}
      form={form}
      employee={employee!}
      contractVersion={contractVersion}
      legalEntityContractData={legalEntityContractData}
      title={i18n.t('contract_versions.flows.request_promote.header.title', {
        employeeFullName: employeeUser(employee).fullName(),
      })}
      flow={requestPromoteContractFlow}
      footerContent={<Footer flow={requestPromoteContractFlow} employee={employee!} />}
      isCurrentStepCompleted={isCurrentStepCompleted}
      isSubmitting={isSubmitting}
      onClose={onClose}
      onSubmit={() => onSubmit()}
    />
  )
}

export default observer(RequestPromoteFlowContent)
